import React, {FC} from 'react';
import { useNavigate } from "react-router-dom";
import {blogGridClasses, StyledBlogGrid} from "./StyledBlogGrid";

import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import {Card, CardContent, CardMedia, Button, Grid, Typography, Box, Divider} from "@mui/material";
import {IBlog} from "../IBlog";

import { NavigationRoutes } from "../../routes";


interface IBlogGrid  {
    blogs: IBlog[]
}

export const BlogGrid: FC<IBlogGrid> = ({blogs}) => {
    const navigate = useNavigate();

    const clickTag = (keyword: string) => (event: React.MouseEvent) => {
        event.stopPropagation();
        const formatKeywordForUrl = (keywordValue: string): string => {
            return keywordValue.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        };
        const formattedKeyword = formatKeywordForUrl(keyword);
        navigate(`/${NavigationRoutes.Blog}/${formattedKeyword}`);
    };

    // Function for handling blog navigation
    const navigateToBlog = (blogSlug: string) => {
        navigate(`/${NavigationRoutes.SinlgeBlog}/${blogSlug}`);
        window.scrollTo(0, 0);
    };

    // Format date from YYYY-MM-DD to "Dec 2025" format
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${month} ${year}`;
    };
    
    return <StyledBlogGrid 
        container 
        spacing={0} 
        columnSpacing={4} 
        sx={{
            display: 'grid',
            gridTemplateColumns: {
                xs: '1fr',           // mobile: 1 column
                md: 'repeat(2, 1fr)', // desktop: 3 columns
                lg: 'repeat(3, 1fr)', // desktop: 3 columns
            },
            rowGap: 40,
            marginTop: { 
                xs: "50px", 
                md: "70px",
                lg: "100px",
            },
            marginBottom: '80px',
        }}
        className={blogGridClasses.root} id="blogGrid">

        {blogs.slice().map((blog) => (        
            <Grid item key={blog.slug} sx={{ 
                
            }}>
                <Card className={blogGridClasses.card}>
                    <CardMedia
                        component="img"
                        image={blog.image}
                        alt={blog.title}
                        className={blogGridClasses.cardImg}
                        onClick={() => blog.slug && navigateToBlog(blog.slug)}
                        style={{ cursor: 'pointer' }}
                    />
                    
                    <CardContent className={blogGridClasses.cardContent}>
                        {/* Top row: Keywords | Date | Read time */}
                        <Box className={blogGridClasses.topRow}>
                            <Box className={blogGridClasses.keywords} sx={{ flex: 1 }}>
                                {blog.keywords && blog.keywords.length > 0 && (
                                    <>
                                        {blog.keywords.map((keyword, index) => (
                                            <Typography key={index} className={blogGridClasses.keywordtag} onClick={clickTag(keyword)}>
                                                {keyword}
                                            </Typography>
                                        ))}
                                    </>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Typography variant="body2" className={blogGridClasses.dateText}>
                                    {formatDate(blog.publishedDate)}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <HourglassBottomIcon fontSize="inherit" htmlColor={"#768697"} />
                                    <Typography variant="body2" className={blogGridClasses.readTimeText}>
                                        {blog.reading_time} read
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Separator bar */}
                        <Divider className={blogGridClasses.separator} />

                        {/* Title - full width */}
                        <Typography gutterBottom variant="h6" className={blogGridClasses.title}>
                            {blog.title}
                        </Typography>

                        {/* Excerpt */}
                        <Typography variant="body2" className={blogGridClasses.excerpt}>
                            {blog.excerpt}
                        </Typography>
                    </CardContent>

                    <div>
                        <Button 
                            variant={"contained"} 
                            className={blogGridClasses.button}
                            onClick={() => blog.slug && navigateToBlog(blog.slug)}
                        >
                            Read Full Blog
                        </Button>
                    </div>
                </Card>
            </Grid>
        ))}


    </StyledBlogGrid>;
};

